// X213 (Grant 2026-10-28): the public competition cards (club page, player page) read the ACTIVE cycle's count only,
// so between cycles every box league / americano said "0 players". The API now sends the shown cycle (the admin
// dashboard's X179 rule: running, else the next one, else the last ended) as shownCycleStatus / StartDate / Entrants /
// JoinOpen. One line: format · sport · status-or-count, parts missing are left out (Grant: "The sport name would be
// helpful in that subtitle as well"). A count shows only for a running cycle, or an upcoming one with people signed up;
// otherwise a plain status in player words. A draft never shows its details publicly: "Coming soon".
import {countNoun} from '~/utils/adminActionCopy';
import {sportDisplayName} from '~/composables/useSportDisplay';

const JOINING_OPEN = 'Joining open';
const OPEN_TO_JOIN = 'Open to join';

/** "players" for singles, "teams" for doubles and up. */
export function sideNoun(playersPerTeam) {
  return (playersPerTeam ?? 1) > 1 ? 'team' : 'player';
}

/** "Mon 3 Nov" (24h app, no year), in the club's zone when known. */
export function cardDate(iso, timeZone) {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat('en-GB', {timeZone: timeZone || undefined, weekday: 'short', day: 'numeric', month: 'short'})
      .format(new Date(iso)).replace(',', '');
  } catch {
    return new Intl.DateTimeFormat('en-GB', {weekday: 'short', day: 'numeric', month: 'short'}).format(new Date(iso)).replace(',', '');
  }
}

/**
 * The status/count words for a box league or americano row.
 * status: running | upcoming | settingUp | finished | null (no cycle). noun: 'player' | 'team'.
 */
export function cycleWords(row, noun, {timeZone, now = new Date()} = {}) {
  const status = row?.shownCycleStatus;
  const entrants = row?.shownCycleEntrants ?? 0;
  const joinOpen = !!row?.shownCycleJoinOpen;
  // An API without the X213 fields: the old active-cycle count, but never a bare 0.
  if (status === undefined) {
    const old = row?.teamCount ?? row?.playerCount;
    return old ? [countNoun(old, noun)] : [];
  }
  const startsLater = row?.shownCycleStartDate && new Date(row.shownCycleStartDate) > now;
  const starts = startsLater ? `Next cycle starts ${cardDate(row.shownCycleStartDate, timeZone)}` : null;
  switch (status) {
    case 'running':
      if (entrants > 0) return [countNoun(entrants, noun)];
      return joinOpen ? [JOINING_OPEN] : [];
    case 'upcoming':
      return [starts, entrants > 0 ? `${countNoun(entrants, noun)} signed up` : (joinOpen ? JOINING_OPEN : null)].filter(Boolean);
    case 'settingUp': {
      // Published with nobody seated yet (dates known), or a draft (no dates sent): "Coming soon".
      const parts = [starts, joinOpen ? JOINING_OPEN : null].filter(Boolean);
      return parts.length ? parts : ['Coming soon'];
    }
    case 'finished':
      return ['No cycle running'];
    default:
      return ['Coming soon'];
  }
}

/** Count words for a ladder / tournament / meet: "10 players", none yet = "Open to join" when it has a link, else nothing. */
export function entrantWords(count, noun, joinable) {
  if (count > 0) return [countNoun(count, noun)];
  return joinable ? [OPEN_TO_JOIN] : [];
}

const sportOf = (row) => (row?.sport?.name ? sportDisplayName(row.sport) : null);
const line = (parts) => parts.filter(Boolean).join(' · ');

export function leagueCardLine(league, opts) {
  return line(['Box league', sportOf(league), ...cycleWords(league, sideNoun(league?.playersPerTeam), opts)]);
}

export function americanoCardLine(americano, opts) {
  return line(['Americano league', sportOf(americano), ...cycleWords(americano, 'player', opts)]);
}

export function ladderCardLine(ladder) {
  return line(['Ladder', sportOf(ladder), ...entrantWords(ladder?.teamCount, sideNoun(ladder?.playersPerTeam), !!ladder?.hasVisibleInviteLink)]);
}

export function tournamentCardLine(tournament) {
  return line(['Tournament', sportOf(tournament), ...entrantWords(tournament?.teamCount, sideNoun(tournament?.playersPerTeam), !!tournament?.hasVisibleInviteLink)]);
}

/** Meet: "Meet · Tennis · every Wednesday · £6.50 · 4 players" (recurrence + fee come from useMeetFormat). */
export function meetCardLine(meet, {recurrence, fee} = {}) {
  return line(['Meet', sportOf(meet), recurrence, fee, ...entrantWords(meet?.playerCount, 'player', !!meet?.hasVisibleInviteLink)]);
}
